import { LinkOutlined, MinusCircleOutlined, PlusOutlined, UploadOutlined } from "@ant-design/icons";
import {
  Button,
  Collapse,
  Form,
  Input,
  InputNumber,
  Modal,
  Select,
  Space,
  Switch,
  Upload,
  Tag,
  message,
  Divider
} from "antd";
import { useEffect, useState } from "react";

import CodeImplementationField from "src/components/common-utils/CodeImplementationField";
import SlugFormItem from "src/components/CourseContentManagement/ModulesManagement/SlugFormItem";
import TranscriptPreview from "src/components/CourseContentManagement/ModulesManagement/TranscriptPreview";
import { selectFormOptions } from "src/utils/helper";

const { Option } = Select;
const { Panel } = Collapse;
const ALLOWED_LECTURE_EDUCATORS = ["Priyansh", "Viraj"];

const CreateEditLectureModal = ({
  open,
  onCancel = () => {},
  onCreate = () => {},
  loading = false,
  initialValues = {},
  courseEducators = [],
  lectureTypes = [],
  isEdit = false,
  onFileUpload = () => {}
}) => {
  const [form] = Form.useForm();
  const [slideUploading, setSlideUploading] = useState(false);
  const [transcriptUploading, setTranscriptUploading] = useState(false);
  const [transcriptChunks, setTranscriptChunks] = useState([]);
  const [transcriptSummary, setTranscriptSummary] = useState("");

  const lectureTypeOptions = selectFormOptions(lectureTypes);
  const defaultLectureTypeValue = lectureTypes[0] || "THEORY";

  const handleOk = () => {
    form
      .validateFields()
      .then((values) => {
        onCreate(values);
      })
      .catch(() => {
        // Validation failed
      });
  };

  useEffect(() => {
    if (open) {
      form.resetFields();
      setTranscriptChunks([]);
      setTranscriptSummary("");
      if (initialValues && Object.keys(initialValues).length > 0) {
        form.setFieldsValue(initialValues);
      } else {
        form.setFieldsValue({
          lectureType: defaultLectureTypeValue,
          isVisible: true,
          isPreviewAvailable: false,
          lectureSetDisplayOrder: 1,
          resources: {
            codeSnippets: []
          },
          aiDoubtSupportMetadata: {}
        });
      }
    }
  }, [open, initialValues, form, defaultLectureTypeValue]);

  return (
    <Modal
      open={open}
      title={isEdit ? "Edit Lecture" : "Create Lecture"}
      okText={isEdit ? "Update" : "Create"}
      cancelText="Cancel"
      onCancel={onCancel}
      onOk={handleOk}
      confirmLoading={loading}
      width={800}
      maskClosable={false}
    >
      <Form form={form} layout="vertical" name="create_edit_lecture_form">
        <Form.Item
          name="title"
          label="Title"
          rules={[{ required: true, message: "Please input the title!" }]}
        >
          <Input />
        </Form.Item>

        <SlugFormItem
          isEdit={isEdit}
          entityName="lecture"
          reservedSlugs={initialValues?.reservedSlugs}
        />

        <Form.Item
          name="lectureType"
          label="Lecture Type"
          rules={[{ required: true, message: "Please select the lecture type!" }]}
          tooltip="Theory teaches a concept. Problem Solving walks through the solution of a problem."
        >
          <Select options={lectureTypeOptions} />
        </Form.Item>

        <Form.Item
          name="tpStreamsAssetId"
          label="TP Streams Asset ID"
          rules={[{ required: true, message: "Please input the TP Streams Asset ID!" }]}
        >
          <Input />
        </Form.Item>

        <Form.Item
          name="educatorId"
          label="Educator"
          rules={[{ required: true, message: "Please select an educator!" }]}
        >
          <Select placeholder="Select Educator" showSearch optionFilterProp="children">
            {courseEducators
              .filter((educator) =>
                ALLOWED_LECTURE_EDUCATORS.some((name) =>
                  educator.name?.toLowerCase().includes(name.toLowerCase())
                )
              )
              .map((educator) => (
                <Option key={educator._id} value={educator._id}>
                  {educator.name}
                </Option>
              ))}
          </Select>
        </Form.Item>

        <Space wrap>
          <Form.Item
            name="lectureSetDisplayOrder"
            label="Display Order"
            rules={[{ required: true, message: "Please input the display order!" }]}
          >
            <InputNumber min={1} />
          </Form.Item>

          <Form.Item name="isVisible" label="Visible" valuePropName="checked">
            <Switch />
          </Form.Item>

          <Form.Item
            name="isPreviewAvailable"
            label="Preview"
            valuePropName="checked"
            tooltip="When ON, non-enrolled users can see the title and watch the video for free."
          >
            <Switch />
          </Form.Item>
        </Space>

        <Form.Item
          name={["resources", "slideUrl"]}
          label="Slide URL"
          rules={[{ type: "url", message: "Please enter a valid URL!" }]}
        >
          <Input placeholder="Enter URL or upload a file below" />
        </Form.Item>

        <Form.Item
          label="Upload Slide File (PDF)"
          extra={
            !isEdit
              ? "Save the lecture first to upload resources"
              : "Upload a new file to replace the existing one"
          }
        >
          <Space direction="vertical" style={{ width: "100%" }}>
            <Form.Item
              noStyle
              shouldUpdate={(prevValues, currentValues) =>
                prevValues.resources?.slideUrl !== currentValues.resources?.slideUrl
              }
            >
              {({ getFieldValue }) => {
                const slideUrl = getFieldValue(["resources", "slideUrl"]);
                return slideUrl ? (
                  <a href={slideUrl} target="_blank" rel="noopener noreferrer">
                    <Tag icon={<LinkOutlined />} color="blue">
                      View Current Slide PDF
                    </Tag>
                  </a>
                ) : null;
              }}
            </Form.Item>
            <Upload
              disabled={!isEdit}
              beforeUpload={async (file) => {
                setSlideUploading(true);
                try {
                  const res = await onFileUpload(file, initialValues.uniquePublicId, "SLIDE");
                  const url = typeof res === "string" ? res : res?.hostedUrl;
                  form.setFieldsValue({
                    resources: {
                      ...(form.getFieldValue("resources") || {}),
                      slideUrl: url
                    }
                  });
                  message.success("File uploaded successfully");
                } catch (err) {
                  // Error handled in parent
                } finally {
                  setSlideUploading(false);
                }
                return false;
              }}
              showUploadList={false}
            >
              <Button icon={<UploadOutlined />} loading={slideUploading} disabled={!isEdit}>
                Click to Upload
              </Button>
            </Upload>
          </Space>
        </Form.Item>

        <Divider orientation="left">AI Doubt Support Metadata</Divider>

        <Form.Item
          name={["aiDoubtSupportMetadata", "transcriptFileUrl"]}
          label="Lecture Transcript URL"
          rules={[{ type: "url", message: "Please enter a valid URL!" }]}
        >
          <Input placeholder="Enter URL or upload a file below" />
        </Form.Item>

        <Form.Item
          label="Upload Lecture Transcript (VTT)"
          extra={
            !isEdit
              ? "Save the lecture first to upload resources"
              : "Upload a new file to replace the existing one"
          }
        >
          <Space direction="vertical" style={{ width: "100%" }}>
            <Upload
              disabled={!isEdit}
              beforeUpload={async (file) => {
                setTranscriptUploading(true);
                try {
                  const res = await onFileUpload(file, initialValues.uniquePublicId, "TRANSCRIPT");
                  const url = typeof res === "string" ? res : res?.hostedUrl;
                  const chunks = Array.isArray(res) ? res : res?.preview || [];
                  const summary = res?.summary || "";

                  form.setFieldsValue({
                    aiDoubtSupportMetadata: {
                      ...(form.getFieldValue("aiDoubtSupportMetadata") || {}),
                      transcriptFileUrl: url
                    }
                  });
                  setTranscriptChunks(chunks);
                  setTranscriptSummary(summary);
                  message.success("File uploaded successfully");
                } catch (err) {
                  // Error handled in parent
                } finally {
                  setTranscriptUploading(false);
                }
                return false;
              }}
              showUploadList={false}
            >
              <Button icon={<UploadOutlined />} loading={transcriptUploading} disabled={!isEdit}>
                Click to Upload
              </Button>
            </Upload>

            <TranscriptPreview
              chunks={transcriptChunks}
              summary={transcriptSummary}
              onClear={() => {
                setTranscriptChunks([]);
                setTranscriptSummary("");
              }}
            />
          </Space>
        </Form.Item>

        <Form.List name={["resources", "codeSnippets"]}>
          {(fields, { add, remove }) => (
            <>
              {fields.map(({ key, name, ...restField }) => (
                <Collapse key={key} style={{ marginBottom: 16 }}>
                  <Panel
                    forceRender
                    header={`Snippet ${extractTitle(form, name) || key + 1}`}
                    extra={
                      <MinusCircleOutlined
                        onClick={(e) => {
                          e.stopPropagation();
                          remove(name);
                        }}
                      />
                    }
                  >
                    <Form.Item
                      {...restField}
                      name={[name, "snippetTitle"]}
                      label="Snippet Title (Optional)"
                    >
                      <Input />
                    </Form.Item>
                    <Form.Item
                      {...restField}
                      name={[name, "displayOrder"]}
                      label="Order"
                      rules={[{ required: true, message: "Missing order" }]}
                    >
                      <InputNumber min={1} />
                    </Form.Item>

                    <CodeImplementationField name={[name, "implementations"]} />
                  </Panel>
                </Collapse>
              ))}
              <Form.Item>
                <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                  Add Code Snippet
                </Button>
              </Form.Item>
            </>
          )}
        </Form.List>
      </Form>
    </Modal>
  );
};

// Helper to get title for dynamic panel header
const extractTitle = (form, index) => {
  const snippets = form.getFieldValue(["resources", "codeSnippets"]) || [];
  return snippets[index]?.snippetTitle;
};

export default CreateEditLectureModal;
